import React from 'react';

const COLORS = {
  ink: '#5A4A42',
  muted: '#A8988D',
  border: '#C8B5A0',
  path: '#B08968',
};

const labelStyle = {
  fontSize: '0.55rem',
  color: COLORS.muted,
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  lineHeight: 1.3,
};

function UnitBadge({ unit }) {
  return (
    <span
      data-count-unit-static="1"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        maxWidth: '100%',
        border: `1px solid ${COLORS.border}`,
        borderRadius: 6,
        padding: 0,
        color: COLORS.ink,
        fontSize: '0.72rem',
        fontWeight: 700,
        lineHeight: 1.15,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      title={unit}
    >
      {unit}
    </span>
  );
}

function HorizontalPath() {
  return (
    <svg aria-hidden="true" viewBox="0 0 28 12" width="28" height="12" style={{ display: 'block' }}>
      <path d="M1 6h20" fill="none" stroke={COLORS.path} strokeWidth="1.5" />
      <path d="m18 2 5 4-5 4" fill="none" stroke={COLORS.path} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function VerticalPath() {
  return (
    <svg aria-hidden="true" viewBox="0 0 12 18" width="12" height="18" style={{ display: 'block' }}>
      <path d="M6 1v10" fill="none" stroke={COLORS.path} strokeWidth="1.5" />
      <path d="m2 9 4 5 4-5" fill="none" stroke={COLORS.path} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CountSeveralLink({ onCountSeveral, entryCount, label }) {
  if (typeof onCountSeveral !== 'function') return null;
  return (
    <button
      type="button"
      data-count-several-link="1"
      onClick={(event) => { event.stopPropagation(); onCountSeveral(); }}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        marginTop: 2,
        color: COLORS.path,
        fontSize: '0.58rem',
        fontWeight: 700,
        textDecoration: 'underline',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {label || 'Count several packages'}
      {entryCount > 1 ? ` (${entryCount})` : ''}
    </button>
  );
}

/**
 * The count card's Vendor / Count-as pair.
 *
 * Count as is the interactive node (`countAsNode`, a CountFormatControl):
 * what the counter chooses here is the PACKAGE the line is counted in, and
 * nothing else — it never selects or preselects who the item is ordered
 * from. Vendor is passive context (the line's supplier of record); the order
 * path is chosen in Savings Review, not on a count card. Legacy hosts that
 * still pass an interactive `vendorNode` with a static `unit` keep the old
 * rendering.
 *
 * `onCountSeveral` (optional) renders the mixed-stock link under the unit —
 * one old Sysco case + half a US Foods case + two loose bags is the normal
 * state of a shelf mid-transition, and the entry sheet is where those stay
 * auditable as separate entries while the line keeps one normalized total.
 * `countSeveralLabel` names the action in the shelf's own words ("Count cases
 * + loose bottles") when the counted package has a discrete rung below it.
 */
export default function VendorUnitFlow({
  vendorLabel = 'Vendor',
  vendorNode = null,
  vendorValue = 'N/A',
  unit = 'each',
  countAsNode = null,
  mobile = false,
  onCountSeveral = null,
  entryCount = 0,
  countSeveralLabel = null,
}) {
  const vendor = vendorNode || (
    <span
      style={{
        color: COLORS.ink,
        fontSize: '0.72rem',
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
      }}
      title={vendorValue}
    >
      {vendorValue}
    </span>
  );

  // Interactive Count-as means the two nodes are independent decisions, so
  // the arrow (a dependency claim) goes away with the old coupling.
  const ariaLabel = countAsNode
    ? 'Vendor and the package this line is counted in'
    : 'Vendor choice may change the Count as unit';
  const countAs = countAsNode || <UnitBadge unit={unit} />;

  if (mobile) {
    return (
      <div
        data-vendor-unit-flow="1"
        data-layout="mobile"
        role="group"
        aria-label={ariaLabel}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}
      >
        <span style={labelStyle}>{vendorLabel}</span>
        <div style={{ display: 'flex', justifyContent: 'center', minWidth: 0, maxWidth: '100%' }}>
          {vendor}
        </div>
        {countAsNode ? <div style={{ height: 4 }} /> : <VerticalPath />}
        <span style={labelStyle}>Count as</span>
        {countAs}
        <CountSeveralLink onCountSeveral={onCountSeveral} entryCount={entryCount} label={countSeveralLabel} />
      </div>
    );
  }

  return (
    <div
      data-vendor-unit-flow="1"
      data-layout="web"
      role="group"
      aria-label={ariaLabel}
      style={{
        display: 'grid',
        gridTemplateColumns: '64px minmax(0, 1fr) 28px auto',
        columnGap: 8,
        alignItems: 'center',
        minWidth: 0,
        marginBottom: 2,
      }}
    >
      <span className="fw-semibold small" style={{ color: '#6D5A4E', textAlign: 'left' }}>
        {vendorLabel}
      </span>
      <div style={{ display: 'flex', minWidth: 0 }}>{vendor}</div>
      {countAsNode ? <span aria-hidden="true" /> : <HorizontalPath />}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
        <span style={labelStyle}>Count as</span>
        {countAs}
        <CountSeveralLink onCountSeveral={onCountSeveral} entryCount={entryCount} label={countSeveralLabel} />
      </div>
    </div>
  );
}
